import { useEffect, useMemo } from 'react';
import { findBlockByType } from '../helpers';
import { usePersonalizeBlocks } from './useDiscovery';
import { trackCustomEvent } from '@teko/tracking';
import { requestIdleCallback } from '@teko/utils';
import { BlockType, DiscoveryBlock, PageTypeEnum } from '@teko/discovery';

const trackDiffBlocks = (
  clientBlocks?: DiscoveryBlock[],
  serverBlocks?: DiscoveryBlock[],
) => {
  // This statement is used to track block mismatches between client and server
  if (clientBlocks?.length && serverBlocks?.length) {
    const clientBlocksIdMap = new Set(clientBlocks.map(({ id }) => id));
    const serverBlocksIdMap = new Set(serverBlocks.map(({ id }) => id));

    const trackBlocks = () => {
      trackCustomEvent('homeBlocksDiff', {
        label: JSON.stringify({
          serverBlocks: Array.from(serverBlocksIdMap),
          clientBlocks: Array.from(clientBlocksIdMap),
        }),
      });
    };

    if (clientBlocksIdMap.size !== serverBlocksIdMap.size) {
      trackBlocks();
    } else {
      for (const id of serverBlocksIdMap) {
        if (!clientBlocksIdMap.has(id)) {
          trackBlocks();
          break;
        }
      }
    }
  }
};

export const useHomePage = (serverBlocks: DiscoveryBlock[]) => {
  const { result: personalizedHomePage } = usePersonalizeBlocks({
    pageTypeEnum: PageTypeEnum.HOME,
  });

  const blocks = useMemo(() => {
    if (personalizedHomePage?.length) {
      return personalizedHomePage.map((block) => ({
        ...block,
        ssr: !!serverBlocks.find((ssrBlock) => ssrBlock.id === block.id),
      }));
    } else return serverBlocks.map((block) => ({ ...block, ssr: true }));
  }, [personalizedHomePage, serverBlocks]);

  const slideBanners = useMemo(() => {
    const banner = findBlockByType(BlockType.WIDE_THICK_BANNER, blocks);
    if (banner) {
      return banner?.cookedData || [];
    }
    return [];
  }, [blocks]);

  const topRightBanners = useMemo(() => {
    const banner = findBlockByType(BlockType.VERTICAL_2_BANNER, blocks);
    if (banner) {
      return banner?.cookedData || [];
    }
    return [];
  }, [blocks]);

  const popupBanner = useMemo(() => {
    return findBlockByType(BlockType.POPUP_BANNER, blocks);
  }, [blocks]);

  const horizontalFourBanners = useMemo(() => {
    return (
      findBlockByType(BlockType.HORIZONTAL_4_BANNER, blocks)?.cookedData || []
    );
  }, [blocks]);

  useEffect(() => {
    function trackDiff(deadline: IdleDeadline) {
      if (deadline.timeRemaining() > 5 || deadline.didTimeout) {
        trackDiffBlocks(personalizedHomePage, serverBlocks);
      } else {
        requestIdleCallback(trackDiff);
      }
    }
    requestIdleCallback(trackDiff);
  }, [personalizedHomePage, serverBlocks]);

  return {
    slideBanners,
    topRightBanners,
    blocks,
    popupBanner,
    horizontalFourBanners,
  };
};

export const useMobileDefaultHomePage = (serverBlocks: DiscoveryBlock[]) => {
  const { result: personalizedHomePage, isLoading: loadingHomePage } =
    usePersonalizeBlocks({ pageTypeEnum: PageTypeEnum.HOME });
  const loading = !serverBlocks && loadingHomePage;

  const blocks = useMemo(() => {
    if (personalizedHomePage?.length) {
      return personalizedHomePage.map((block) => ({
        ...block,
        ssr: !!serverBlocks.find((ssrBlock) => ssrBlock.id === block.id),
      }));
    } else return serverBlocks.map((block) => ({ ...block, ssr: true }));
  }, [personalizedHomePage, serverBlocks]);

  useEffect(() => {
    function trackDiff(deadline: IdleDeadline) {
      if (deadline.timeRemaining() > 5 || deadline.didTimeout) {
        trackDiffBlocks(personalizedHomePage, serverBlocks);
      } else {
        requestIdleCallback(trackDiff);
      }
    }
    requestIdleCallback(trackDiff);
  }, [personalizedHomePage, serverBlocks]);

  return {
    blocks,
    loading,
  };
};
